import fiatCurrencies from '@exodus/fiat-currencies';
import { isEthereumLikeToken } from '@exodus/ethereum-lib';
import { conversionByRate } from '@exodus/currency';
import { SwapMode } from '@exodus/exchange-client-providers/lib/common/types';
const CHFV2__PROVIDER_ADDRESS = '0x99870de8ae594e6e8705fc6689e89b4d039af1e2';
const toUsd = (amount, assetPrices) => {
    const convert = conversionByRate(amount.unitType, fiatCurrencies.USD, assetPrices.USD);
    return convert(amount);
};
export class ExchangeQuotes {
    #activeWalletAccountAtom;
    #addressProvider;
    #exchangeAssets;
    #exchangeGetAllowance;
    #exchangeProviders;
    #exchangeQuotesAtom;
    #exchangeFees;
    #pricing;
    #walletAccounts;
    constructor(params) {
        this.#activeWalletAccountAtom = params.activeWalletAccountAtom;
        this.#addressProvider = params.addressProvider;
        this.#exchangeAssets = params.exchangeAssets;
        this.#exchangeGetAllowance = params.exchangeGetAllowance;
        this.#exchangeProviders = params.exchangeProviders;
        this.#exchangeQuotesAtom = params.exchangeQuotesAtom;
        this.#exchangeFees = params.exchangeFees;
        this.#pricing = params.pricingClient;
        this.#walletAccounts = params.walletAccounts;
    }
    getBestQuote = async (params) => {
        const { quoteId, fromAssetName, toAssetName, amount, slippage, isSendAll, swapMode = SwapMode.ExactIn, } = params;
        const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
        const toAsset = this.#exchangeAssets.getAsset({ assetName: toAssetName });
        const activeWalletAccount = await this.#activeWalletAccountAtom.get();
        const fromWalletAccount = params.fromWalletAccount || activeWalletAccount;
        const toWalletAccount = params.toWalletAccount || activeWalletAccount;
        const [fromAddress, toAddress] = await Promise.all([
            this.#addressProvider.getReceiveAddress({
                assetName: fromAsset.name,
                walletAccount: this.#walletAccounts.get(fromWalletAccount),
                useCache: true,
            }),
            this.#addressProvider.getReceiveAddress({
                assetName: toAsset.name,
                walletAccount: this.#walletAccounts.get(toWalletAccount),
                useCache: true,
            }),
        ]);
        await this.#exchangeQuotesAtom.set((prev) => ({
            ...prev,
            [quoteId]: { quote: prev[quoteId]?.quote || null, loading: true },
        }));
        let lessQuoteLosses;
        const quotesQueue = [];
        const handleQuote = async (quote) => {
            const quoteQueuePromise = new Promise(async (resolve) => {
                const losses = await this.getQuoteLosses({
                    amount,
                    fromAsset,
                    quote,
                    toAsset,
                    swapMode,
                }).catch(() => {
                    return fromAsset.currency.ZERO;
                });
                if (!lessQuoteLosses || losses.lt(lessQuoteLosses)) {
                    lessQuoteLosses = losses;
                    await this.#exchangeQuotesAtom.set((prev) => ({
                        ...prev,
                        [quoteId]: { loading: true, quote },
                    }));
                }
                resolve();
            });
            quotesQueue.push(quoteQueuePromise);
        };
        const handleFinished = async () => {
            await Promise.all(quotesQueue);
            await this.#exchangeQuotesAtom.set((prev) => {
                const quote = prev[quoteId];
                return {
                    ...prev,
                    [quoteId]: { ...quote, loading: false },
                };
            });
        };
        void this.#exchangeProviders.getQuotes({
            amount,
            fromAddress: fromAddress.toString(),
            swapMode,
            getAllowance: (args) => this.#exchangeGetAllowance.getAllowance({ walletAccount: fromWalletAccount, ...args }),
            getFeeOpts: (params) => {
                const asset = this.#exchangeAssets.getAsset({ assetId: params.assetId });
                const isBaseAsset = asset.baseAsset.name === asset.name;
                const { pidBytes, ...feeOptsParams } = params;
                let txInput;
                if (pidBytes) {
                    const _txInput = isBaseAsset
                        ? []
                        : isEthereumLikeToken(asset)
                            ? asset.contract.transfer.build(params.toAddress?.toLowerCase() || CHFV2__PROVIDER_ADDRESS, params.amount.toBaseString({ unit: false }))
                            : [];
                    txInput = Buffer.from(new Uint8Array([..._txInput, ...params.pidBytes]));
                }
                return this.#exchangeFees.getFeeOpts({
                    ...feeOptsParams,
                    txInput,
                    walletAccount: fromWalletAccount,
                    isExchange: true,
                });
            },
            onFinish: handleFinished,
            onQuote: handleQuote,
            pairId: `${fromAsset.ticker}_${toAsset.ticker}`,
            slippage,
            toAddress: toAddress.toString(),
            isSendAll,
        });
    };
    getQuoteLosses = async (params) => {
        const { fromAsset, toAsset, amount, quote, swapMode = SwapMode.ExactIn } = params;
        const fromAssetId = fromAsset.ticker;
        const toAssetId = toAsset.ticker;
        const prices = await this.#pricing.currentPrice({
            assets: [...new Set([fromAsset.feeAsset.ticker, fromAssetId, toAssetId])],
        });
        [fromAssetId, toAssetId, fromAsset.feeAsset.ticker].forEach((assetId) => {
            if (!prices[assetId]) {
                throw new Error(`There is no price data for the assets ${assetId}`);
            }
        });
        const feeAmount = quote.exchangeFeeData.feeOpts?.fee || fromAsset.feeAsset.currency.ZERO;
        const gasFeeInUsd = toUsd(feeAmount, prices[fromAsset.feeAsset.ticker]);
        let fromAmountInUsd;
        let toAmountInUsd;
        if (swapMode === SwapMode.ExactOut) {
            fromAmountInUsd = toUsd(quote.amount, prices[fromAssetId]);
            toAmountInUsd = toUsd(amount, prices[toAssetId]);
        }
        else {
            fromAmountInUsd = toUsd(amount, prices[fromAssetId]);
            toAmountInUsd = toUsd(quote.amount, prices[toAssetId]);
        }
        return toAmountInUsd.sub(gasFeeInUsd).sub(fromAmountInUsd).negate();
    };
}
const exchangeQuotesModuleFactory = (args) => new ExchangeQuotes({ ...args });
const exchangeQuotesModuleDefinition = {
    id: 'exchangeQuotes',
    type: 'module',
    factory: exchangeQuotesModuleFactory,
    dependencies: [
        'activeWalletAccountAtom',
        'addressProvider',
        'exchangeAssets',
        'exchangeGetAllowance',
        'exchangeProviders',
        'exchangeQuotesAtom',
        'exchangeFees',
        'walletAccounts',
        'pricingClient',
    ],
};
export default exchangeQuotesModuleDefinition;
